import { addComp, removeComp } from "@/api";
import AdjustmentSteps from "@/components/AdjustmentSteps";
import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import { PropertyHeader } from "@/components/PropertyHeader";
import StepTitleSection from "@/components/StepTitleSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import useFetch from "@/hooks/useFetch";
import { formatDate, formatPrice } from "@/lib/formatters";
import { normalizeDataBasedOnVersion, NormalizedDataPoint } from "@/lib/PreparedDataset";
import { cn } from "@/lib/utils";
import { PageProps } from "@/types/PageProps";
import { Adjustment, AdjustmentProperty } from "@/types/serializers";
import { Head, router } from "@inertiajs/react";
import { ArrowRightIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { GrHelpBook } from "react-icons/gr";

type Comp = { id: string };
type Props = PageProps & {
  adjustment: Adjustment;
  dataset_url: string | null;
  comps: Comp[];
};

function CompTable({
  sheet,
  comps,
  adjustment,
}: {
  sheet: NormalizedDataPoint[];
  comps: Comp[];
  adjustment: AdjustmentProperty;
}) {
  const [filter, setFilter] = useState("");
  const filteredSheet = sheet.filter((row) => {
    return row.address.toLowerCase().includes(filter.toLowerCase());
  });
  return (
    <div className="space-y-4">
      <div className="flex space-x-2">
        <Input placeholder="Search" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {filter && (
          <Button variant="outline" onClick={() => setFilter("")} className="flex-0">
            Clear Filter
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredSheet.map((row, index) => {
            const isSelected = comps.map((c) => c.id).includes(row.id);
            return (
              <TableRow key={index}>
                <TableCell>
                  <Checkbox
                    checked={isSelected}
                    onClick={async () => {
                      if (isSelected) {
                        await removeComp(adjustment.public_id, row.id);
                      } else {
                        await addComp(adjustment.public_id, row.id);
                      }
                      router.reload();
                    }}
                  />
                </TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell>{formatDate(row.date.getTime())}</TableCell>
                <TableCell>{formatPrice(row.price)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function SelectedComps({
  sheet,
  comps,
  adjustment,
}: {
  sheet: NormalizedDataPoint[] | null;
  comps: Comp[];
  adjustment: AdjustmentProperty;
}) {
  return (
    <div>
      {comps.length > 0 ? (
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Price</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comps.map((comp) => {
                const row = sheet?.find((row) => row.id === comp.id);
                if (!row) return null;
                return (
                  <TableRow key={comp.id}>
                    <TableCell>{row.address}</TableCell>
                    <TableCell>{formatDate(row.date.getTime())}</TableCell>
                    <TableCell>{formatPrice(row.price)}</TableCell>
                    <TableCell className="p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          await removeComp(adjustment.public_id, comp.id);
                          router.reload();
                        }}
                      >
                        <XIcon className="w-2 h-2" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}

export default function Component({ user, adjustment, errors, notice, dataset_url, comps }: Props) {
  const { data: unparsedData, isPending, error } = useFetch(dataset_url);
  const data = unparsedData ? normalizeDataBasedOnVersion(unparsedData) : null;

  const compMin = 3;
  const compMinReached = comps.length >= compMin;

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title={`Comparables - ${adjustment.address}`} />
      <div className="py-4">
        <BackToDashboard />

        <AdjustmentSteps currentStep="comps" adjustment={adjustment} className="mb-4" />

        <PropertyHeader adjustment={adjustment} />

        <div className="flex pt-4 space-x-8 mx-auto">
          <div className="flex-grow">
            <div className="w-full">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">
                    <div>Select the comparable properties</div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isPending && <div>Loading...</div>}
                  {error && <div>Error: {error}</div>}
                  {data && <CompTable sheet={data} comps={comps} adjustment={adjustment} />}
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="max-w-md flex-grow">
            <div className="w-full space-y-4">
              <StepTitleSection
                title="Comparables"
                description={
                  <div className="space-y-4">
                    <div>Select the comparable properties for your analysis.</div>

                    <div>
                      <Alert variant="muted">
                        <GrHelpBook className="w-5 h-5" />
                        <AlertTitle>Need some help?</AlertTitle>
                        <AlertDescription>
                          <a
                            href="https://teeba-calculator.com/instructions#comparable-properties"
                            className="text-primary font-bold hover:underline"
                            target="_blank"
                          >
                            Click here
                          </a>{" "}
                          to view more detailed instructions.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </div>
                }
              >
                {compMinReached ? (
                  <Button
                    onClick={() => {
                      router.post(`/adjustments/${adjustment.public_id}/comps`);
                    }}
                    className={cn(buttonVariants(), "w-full")}
                  >
                    Next <ArrowRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button className="w-full" disabled={true}>
                    Next <ArrowRightIcon className="w-4 h-4" />
                  </Button>
                )}
              </StepTitleSection>

              {!compMinReached && (
                <div className="text-muted-foreground text-sm">
                  To continue, please select at least {compMin} comps.
                </div>
              )}

              <SelectedComps sheet={data} comps={comps} adjustment={adjustment} />
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
